import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import findKey from 'lodash/findKey';
import flatMap from 'lodash/flatMap';
import { Table } from 'semantic-ui-react';
import { Loader, Tab, Flex, Link, Icon, PageTitle } from 'nallian-shared-ui/lib/components';
import { history } from 'nallian-shared-ui/lib/config/history';
import { TABS, FEATURE_TABS } from '../../constants';
import FeatureDetails from '../feature/FeatureDetails';
import FeatureRoles from '../feature/FeatureRoles';
import RoleDetails from '../role/RoleDetails';
import FeatureRolesTable from '../feature/FeatureRolesTable';
import AppInstallationCommunities from '../communities/AppInstallationCommunities';

const TAB_INDEX_MAPPING = {
  [FEATURE_TABS.DETAILS]: 0,
  [FEATURE_TABS.ROLES]: 1,
  [FEATURE_TABS.COMMUNITIES]: 2,
};

const SubviewFlex = styled(Flex)`
  && .tab {
    box-shadow: none;
    border: none;
    padding-left: 0.2rem;
    padding-right: 0;
  }
`;

const AppInstallationDetail = ({ appInstallationFetch, appName }) => {
  const { id, installationId, view, viewId, viewPane } = useParams();
  const { t } = useTranslation();

  const { feature, role } = useMemo(() => {
    const flattenedData = flatMap(appInstallationFetch.value?.appInstallationFeatures || [], f =>
      (f.appInstallationFeatureRoles || []).map(r => ({ ...r, feature: f }))
    );

    if (view === 'features') {
      return {
        feature: flattenedData.find(f => f.feature.id === viewId)?.feature,
      };
    }
    if (view === 'roles') {
      const r = flattenedData.find(fr => fr.id === viewId);
      return {
        feature: r?.feature,
        role: r,
      };
    }
    return {};
  }, [view, viewId, appInstallationFetch.value]);

  return (
    <>
      <PageTitle>
        <Flex alignItems="center">
          <Link to="/apps" marginright="1px">
            {t('apps_plural')}
          </Link>
          &nbsp;/&nbsp;
          <Link to={`/apps/${id}`} marginright="1px">
            {appName}
          </Link>
          &nbsp;/&nbsp;
          <Link to={`/apps/${id}/installations`} marginright="1px">
            {t('installations')}
          </Link>
          &nbsp;/&nbsp;
          {role || feature ? (
            <Link to={`/apps/${id}/installations/${installationId}`}>
              {appInstallationFetch.value?.name}
            </Link>
          ) : (
            <span>{appInstallationFetch.value?.name}</span>
          )}
          {role ? (
            <>
              <span>&nbsp;/&nbsp;</span>
              <Link to={`/apps/${id}/installations/${installationId}/features/${feature.id}`}>
                <strong>{feature?.name}</strong>
              </Link>
            </>
          ) : (
            undefined
          )}
        </Flex>
      </PageTitle>
      <Flex fluid direction="column">
        {viewId ? (
          <SubviewFlex fluid direction="column" margin={feature ? '0.8rem 0 0' : '0'}>
            <Tab
              activeIndex={TAB_INDEX_MAPPING[viewPane]}
              onTabChange={(e, { activeIndex }) => {
                const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
                if (activeTab) {
                  history.push(
                    `/apps/${id}/${TABS.INSTALLATIONS}/${installationId}/${view}/${viewId}/${activeTab}`
                  );
                }
              }}
              panes={[
                {
                  menuItem: t('details'),
                  render: () => (
                    <Tab.Pane>
                      {view === 'features' ? (
                        <FeatureDetails
                          feature={feature}
                          isLoading={appInstallationFetch.pending}
                        />
                      ) : (
                        <RoleDetails role={role} isLoading={appInstallationFetch.pending} />
                      )}
                    </Tab.Pane>
                  ),
                },
                view === 'features'
                  ? {
                      menuItem: t('roles'),
                      render: () => (
                        <Tab.Pane>
                          <FeatureRolesTable
                            rootUrl={`/apps/${id}/${TABS.INSTALLATIONS}/${installationId}`}
                            roles={feature?.appInstallationFeatureRoles}
                          />
                        </Tab.Pane>
                      ),
                    }
                  : undefined,
              ]}
            />
          </SubviewFlex>
        ) : (
          <Tab
            panes={[
              {
                menuItem: t('overview'),
                render: () => (
                  <Tab.Pane>
                    <Table basic="very">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width="4">{t('feature')}</Table.HeaderCell>
                          <Table.HeaderCell width="2">{t('isGlobal')}</Table.HeaderCell>
                          <Table.HeaderCell width="2">{t('isCrossCommunity')}</Table.HeaderCell>
                          <Table.HeaderCell width="2">{t('requiresSubscription')}</Table.HeaderCell>
                          <Table.HeaderCell width="8">{t('roles')}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {(appInstallationFetch.value?.appInstallationFeatures || []).map(f => (
                          <Table.Row key={`feature_${f.id}`}>
                            <Table.Cell width="4">
                              <Link
                                to={`/apps/${id}/${TABS.INSTALLATIONS}/${installationId}/features/${f.id}/${FEATURE_TABS.DETAILS}`}
                              >
                                {f.name}
                              </Link>
                            </Table.Cell>
                            <Table.Cell width="2">
                              <Icon
                                name={f.isGlobal ? 'checkmark' : 'minus'}
                                color={f.isGlobal ? 'green' : 'gray'}
                              />
                            </Table.Cell>
                            <Table.Cell width="2">
                              <Icon
                                name={f.isCrossCommunity ? 'checkmark' : 'minus'}
                                color={f.isCrossCommunity ? 'green' : 'gray'}
                              />
                            </Table.Cell>
                            <Table.Cell width="2">
                              <Icon
                                name={f.requiresSubscription ? 'checkmark' : 'minus'}
                                color={f.requiresSubscription ? 'green' : 'gray'}
                              />
                            </Table.Cell>
                            <Table.Cell width="8">
                              <FeatureRoles
                                rootUrl={`/apps/${id}/${TABS.INSTALLATIONS}/${installationId}`}
                                value={f.appInstallationFeatureRoles}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        {appInstallationFetch.pending && (
                          <Flex fluid justifyContent="center">
                            <Loader inline active />
                          </Flex>
                        )}
                        {!appInstallationFetch.pending &&
                          !appInstallationFetch.value?.appInstallationFeatures?.length && (
                            <Table.Row>
                              <Table.Cell>{t('noFeaturesYet')}</Table.Cell>
                            </Table.Row>
                          )}
                      </Table.Body>
                    </Table>
                  </Tab.Pane>
                ),
              },
              {
                menuItem: t('communities'),
                render: () => (
                  <Tab.Pane>
                    <AppInstallationCommunities />
                  </Tab.Pane>
                ),
              },
            ]}
          />
        )}
      </Flex>
    </>
  );
};

AppInstallationDetail.propTypes = {
  appInstallationFetch: PropTypes.object.isRequired,
  appName: PropTypes.string.isRequired,
};

export default AppInstallationDetail;
